import { BreadcrumbsCommon, ClipLoaderCommon } from "@components";
import { IBreadcrumbs, ICustomerDetail } from "@interfaces";
import { AppModuleLayout } from "@layouts";
import { useAppDispatch, useAppSelector } from "@redux";
import { PATH_HOME, PATH_QUOTES, PATH_SHARE_QUOTES_WITH_CONTACT } from "@routes";
import { breakpoint, theme } from "@utilities";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { loadQuoteCustomers, selectQuotes } from "src/Redux/Slices/allQuotes";
import { quoteApi } from "@api";
import styled from "styled-components";

interface StyledSuggestionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isSuggestionClicked: boolean;
    disabled?: boolean;
}

interface Suggestion {
    first_name: string | null;
    last_name: string | null;
    phone_number: number;
    country_code: string;
    email: string;
    id: string;
    quoteId?: number;
    customers?: ICustomerDetail[];
}

const renderHeader = () => {
    return (
        <div className="heading-container">
            <div className="heading">
                <p>Share with friends and family</p>
            </div>
        </div>
    );
};

const Content = () => {
    const { quotesCustomers } = useAppSelector(selectQuotes);
    const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
    const [isSharing, setIsSharing] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchParams] = useSearchParams();
    const quoteId = searchParams.get("quoteId")!;
    const quoteCustomers = _.get(quotesCustomers, quoteId);
    const dispatch = useAppDispatch();

    const filteredCustomers = useMemo(() => {
        const customers = quoteCustomers?.customers ?? [];
        if (!searchTerm.trim()) return customers;
        const term = searchTerm.trim().toLowerCase();
        return customers.filter((user) => {
            const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.toLowerCase();
            const email = (user.email ?? "").toLowerCase();
            return fullName.includes(term) || email.includes(term);
        });
    }, [quoteCustomers?.customers, searchTerm]);

    useEffect(() => {
        if (selectedSuggestion && !filteredCustomers.some((u) => u.id === selectedSuggestion.id)) {
            setSelectedSuggestion(null);
        }
    }, [filteredCustomers, selectedSuggestion]);

    const handleShare = async () => {
        if (!selectedSuggestion) {
            ErrorToast({ Message: "Please select a contact to share with" });
            return;
        }

        try {
            setIsSharing(true);
            await quoteApi.ShareQuotesWithContact({
                quoteId,
                redeemerId: Number(selectedSuggestion.id),
            });
        } catch (error: any) {
            console.error(error);
            const errorMessage = error?.response?.data?.title || "Failed to share quote";
            ErrorToast({ Message: errorMessage });
        } finally {
            setIsSharing(false);
        }
    };

    useEffect(() => {
        const scrollToTop = () => {
            window.scrollTo(0, 0);
        };

        const fetchQuoteCustomers = async () => {
            try {
                await dispatch(loadQuoteCustomers(quoteId)).unwrap();
            } catch (error) {
                console.error(error);
                ErrorToast({ Message: "Failed to load customers" });
            }
        };

        fetchQuoteCustomers();
        scrollToTop();
    }, []);

    return (
        <ShareModal>
            <ShareModalContent>
                <ShareModalHeader>
                    <h3>Select the person to share the quote with</h3>
                </ShareModalHeader>
                <SearchBox>
                    <input
                        type="text"
                        placeholder={"Search Contacts"}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </SearchBox>
                <Recent>
                    <p>Recent</p>
                    <ScrollBox>
                        {filteredCustomers.length > 0 ? (
                            _.map(filteredCustomers, (user) => (
                                <div className="details" key={user.id}>
                                    <span>
                                        <StyledSuggestionButton
                                            type="button"
                                            isSuggestionClicked={selectedSuggestion?.id === user.id}
                                            onClick={() =>
                                                setSelectedSuggestion({
                                                    first_name: user.first_name,
                                                    last_name: user.last_name,
                                                    phone_number: Number(user.phone_number),
                                                    country_code: user.country_code,
                                                    email: user.email,
                                                    id: user.id,
                                                })
                                            }
                                        >
                                            <div />
                                        </StyledSuggestionButton>
                                    </span>
                                    <div>
                                        <h1>
                                            {user.first_name} {user.last_name}
                                        </h1>
                                        <p>{user.email}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <EmptyState>{searchTerm.trim() ? "No matching contacts" : "No contacts available"}</EmptyState>
                        )}
                    </ScrollBox>
                </Recent>
                <ShareContainer>
                    <ShareButton onClick={handleShare} disabled={isSharing}>
                        {isSharing ? (
                            <ClipLoaderCommon color={theme.colors.highlight} size={24} margin={0} />
                        ) : (
                            "Share"
                        )}
                    </ShareButton>
                    <InfoText>
                        If they're not a Freelance user, they'll be invited to join and can get
                        points if they upgrade before booking.
                    </InfoText>
                </ShareContainer>
            </ShareModalContent>
        </ShareModal>
    );
};

export const ShareQuotesWithContact = () => {
    const QuotesBreadcrumbs: IBreadcrumbs[] = [
        { title: "Home", link: PATH_HOME },
        { title: "Quotes", link: PATH_QUOTES },
        { title: "Share", link: PATH_SHARE_QUOTES_WITH_CONTACT },
    ];
    return (
        <AppModuleLayout>
            <TitleContainer>
                <div className="wrapper">
                    <BreadcrumbsWrapper>
                        <div className="breadCrumbs">
                            <BreadcrumbsCommon data={QuotesBreadcrumbs} />
                        </div>
                    </BreadcrumbsWrapper>
                    <div className="subWrapper">
                        <div className="listCarts">
                            {renderHeader()}
                            <Content />
                        </div>
                    </div>
                </div>
            </TitleContainer>
        </AppModuleLayout>
    );
};

const TitleContainer = styled.div`
    padding: 16px;
    background-color: #f2f2f2;
    flex: 1;
    padding-bottom: 80px;

    ${breakpoint.breakOnlyMobile`
        background-color: #fff;
    `}

    .heading-container {
        display: flex;
        justify-content: space-between;
    }

    .wrapper {
        margin: 0 auto;

        ${breakpoint.breakTablet`
            width: ${(p) => p.theme.widths.wide_700};
        `}

        ${breakpoint.breakIpadPro`
            width: ${(p) => p.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(p) => p.theme.widths.wide_1110};
        `}
    }
    .breadCrumbs {
        ${breakpoint.breakTablet`
            margin-top: 14px;
        `}
        ${breakpoint.breakOnlyMobile`
            left: 16px;
        `}
    }
    .subWrapper {
        ${breakpoint.breakTablet`
            display: flex;
            margin-top: 30px;
        `}
    }
    .listCarts {
        flex: 1;
        ${breakpoint.breakTablet`
            margin-top: 0;
        `}

        .button-heading {
            font-size: 16px;
            font-weight: 500;
            line-height: 120%;
            display: flex;
            align-items: center;
            font-family: poppins;
        }

        .heading {
            font-size: 20px;
            font-weight: 700;
            line-height: 36px;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 30px;

            ${breakpoint.breakIpadPro`
                margin-bottom: 25px;
            `}

            ${breakpoint.breakIpadPro`
                font-size: 28px;
            `}
        }
    }
`;

const BreadcrumbsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    .breadCrumbs {
        flex: 1;
    }
`;

const SearchBox = styled.div`
    input {
        width: 100%;
        height: 100%;
        background-color: ${(p) => p.theme.colors.gray_6};
        padding: 12px 14px 12px 14px;
        font-size: 16px;
        border-radius: 4px;

        &::placeholder {
            color: ${(p) => p.theme.colors.gray_3};
        }
    }
`;

const ShareModal = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    ${breakpoint.breakOnlyMobile`
        align-items: flex-end;
    `}
`;

const ShareModalContent = styled.div`
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 700px;

    ${breakpoint.breakOnlyMobile`
        padding: 0px;
        height: 100%;
        flex: 1;
    `}
`;

const ShareModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    h3 {
        font-size: 24px;
        font-weight: 500;

        ${breakpoint.breakOnlyMobile`
            font-size: 16px;    
        `}
    }
    margin-bottom: 20px;
`;

const Recent = styled.div`
    margin-top: 20px;
`;

const StyledSuggestionButton = styled.button<StyledSuggestionButtonProps>`
    width: 24px;
    height: 24px;
    padding: 6px;
    border-radius: 50px;
    border: ${(props) =>
        props.isSuggestionClicked ? `1px solid ${theme.colors.time_radio}` : "1px solid #daddde"};
    transition: background-color 0.3s ease;
    cursor: pointer;
    background-color: ${(props) => (props.isSuggestionClicked ? theme.colors.time_radio : "")};

    /* Mobile touch improvements */
    @media (max-width: 768px) {
        /* Safari mobile touch improvements */
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    div {
        width: 100%;
        height: 100%;
        border-radius: 50px;
        background-color: #fff;
    }
`;

const ScrollBox = styled.div`
    margin-top: 20px;
    max-height: 20vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;

    ${breakpoint.breakOnlyMobile`
        max-height: calc(100vh - 200px);
        padding-bottom: 80px;
    `}

    .details {
        display: flex;
        align-items: center;
        gap: 16px;
        &:last-child {
            margin-bottom: 0;
        }
        div {
            display: flex;
            flex-direction: column;
            /* Use margin instead of gap for Safari mobile compatibility */
            h1 {
                font-size: 24px;
                font-weight: 600;
                margin-bottom: 6px;
            }
            p {
                font-size: 12px;
                color: #6e7375;
                margin-bottom: 0;
            }
        }
    }

    /* Safari mobile scrollbar styling */
    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
    }

    /* Mobile view - use margin instead of gap for Safari compatibility */
    @media (max-width: 768px) {
        gap: 0;
        /* Safari mobile scrollbar fixes */
        -webkit-overflow-scrolling: touch;
        -webkit-scrollbar-width: thin;
        -webkit-scrollbar-color: #d1d5db transparent;

        .details {
            margin-bottom: 20px;
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
`;

const ShareContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${breakpoint.breakOnlyMobile`
        position: fixed;
        bottom: 20px;
        left: 20px;
        right: 20px;
        z-index: 1000;
    `}
`;

const ShareButton = styled.button`
    margin: 20px auto 0;
    width: 250px;
    align-items: center;
    padding: 12px 0;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background: linear-gradient(
        to right,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    color: ${theme.colors.button_text};
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    display: block;

    ${breakpoint.breakOnlyMobile`
        width: 100%;
        margin: 0;
    `}
`;

const InfoText = styled.p`
    margin-top: 20px;
    font-size: 12px;
    color: #666;
    text-align: center;

    ${breakpoint.breakOnlyMobile`
        margin: 10px 0 0 0;
        padding: 0;
    `}
`;

const EmptyState = styled.div`
    text-align: center;
    color: #6e7375;
    padding: 20px;
    font-size: 14px;
`;
